/**
 * Numbering follows the boxes on the ICS 215A-CG form, as printed by
 * `services/coms/src/routes/Forms/ICS_CG/forms/ICS215ACG.ts`.
 *
 * Boxes 1-4 are the read-only header the card renders as its own sections.
 * Boxes 5-8 are columns of the single incident-area table rather than sections,
 * so they are listed separately in `ICS215A_PRINTED_COLUMNS`.
 *
 * Box 9 is the one signature the Coast Guard form prints. The NIMS 215A the
 * docx descriptor is read off splits it into a Safety Officer and an Operations
 * Section Chief box, which is why the card labels the box from here rather than
 * from the descriptor-derived signature slot.
 */
export const ICS215A_BOX_LABELS = {
  incidentName: '1. Incident Name:',
  incidentLocation: '2. Incident Location:',
  dateTimePrepared: '3. Date/Time Prepared:',
  operationalPeriod: '4. Operational Period:',
  incidentAreas: '5-8. Incident Areas:',
  preparedBy: '9. Prepared by:',
} as const;

/**
 * What each printed column holds, which is not always what the card's table
 * shows under the same heading.
 *
 * Worth keeping written down: Risk/Gain is a PDF dropdown rather than a text
 * field, so the export has to select one of its options instead of writing into
 * it, and a value the dropdown does not offer is dropped silently.
 */
export const ICS215A_PRINTED_COLUMNS = [
  { box: 5, label: 'Incident Area', source: 'area_name' },
  { box: 6, label: 'Hazards / Risks', source: 'hazard' },
  { box: 7, label: 'Mitigations', source: 'mitigations' },
  {
    box: 8,
    label: 'Risk/Gain',
    source: 'risk_gain, selected on a PDF dropdown',
  },
] as const;

export const ICS215A_INCIDENT_NAME_HINT =
  'This box is read-only. It is filled from the incident name.';

/** Mirrors `resolveOperationalPeriodValues`, which the PDF export uses. */
export const ICS215A_INCIDENT_LOCATION_HINT =
  'This box is read-only. It is filled from the incident location, falling back to the address set when the incident was created.';

/**
 * The export stamps the moment the PDF is generated, not when a box was last
 * saved.
 */
export const ICS215A_DATE_TIME_PREPARED_HINT =
  'This box is read-only. The export stamps the date and time the form is generated.';

export const ICS215A_OPERATIONAL_PERIOD_HINT =
  'Click this box to set the scheduled start and end of the next operational period for this incident workspace. Working-period forms print that window.';

/**
 * Areas are incident-scoped rather than period-scoped, so the same rows print
 * on every period's 215A. Said plainly on the card so nobody expects a fresh
 * sheet when the period advances.
 */
export const ICS215A_INCIDENT_AREAS_HINT =
  'Areas belong to the incident, not to one operational period, so they carry across every period until they are removed.';

/** Both halves of the printed dropdown, in the order the PDF lists them. */
export const ICS215A_RISK_GAIN_OPTIONS = [
  'Low/Low',
  'Low/Med',
  'Low/High',
  'Med/Low',
  'Med/Med',
  'Med/High',
  'High/Low',
  'High/Med',
  'High/High',
] as const;
